/**
 * uic-auth模块的认证信息管理工具
 * 用于统一管理与uic-auth相关的token和用户信息存储
 */
export const UIC_AUTH_KEYS = {
  accessToken: 'uic-auth',      // uic-auth访问令牌
  refreshToken: 'uic-auth_refresh_token',    // uic-auth刷新令牌
  tokenType: 'uic-auth_token_type',          // uic-auth令牌类型
  userInfo: 'uic-auth_user_info'             // uic-auth关联用户信息
}

/**
 * 获取uic-auth的访问令牌
 * @returns {string|null} 访问令牌
 */
export const getUicAuthToken = () => {
  // 优先从sessionStorage读取会话级token，再从localStorage读取持久化token
  return sessionStorage.getItem(UIC_AUTH_KEYS.accessToken) || 
         localStorage.getItem(UIC_AUTH_KEYS.accessToken) || 
         null
}

/**
 * 获取uic-auth的令牌类型
 * @returns {string} 令牌类型，默认为'bearer'
 */
export const getUicAuthTokenType = () => {
  return sessionStorage.getItem(UIC_AUTH_KEYS.tokenType) || 
         localStorage.getItem(UIC_AUTH_KEYS.tokenType) || 
         'bearer'
}

/**
 * 获取uic-auth的刷新令牌
 * @returns {string|null} 刷新令牌
 */
export const getUicAuthRefreshToken = () => {
  return sessionStorage.getItem(UIC_AUTH_KEYS.refreshToken) || 
         localStorage.getItem(UIC_AUTH_KEYS.refreshToken) || 
         null
}

/**
 * 获取uic-auth关联的用户信息
 * @returns {object|null} 用户信息对象
 */
export const getUicAuthUserInfo = () => {
  try {
    const userInfoStr = sessionStorage.getItem(UIC_AUTH_KEYS.userInfo) || 
                       localStorage.getItem(UIC_AUTH_KEYS.userInfo)
    return userInfoStr ? JSON.parse(userInfoStr) : null
  } catch (error) {
    console.error('解析uic-auth用户信息失败:', error)
    return null
  }
}

/**
 * 从URL参数中获取用户ID并保存到本地存储
 * 用于处理登录后无法获取用户信息的情况
 */
export const saveUserInfoFromUrl = () => {
  try {
    console.log('开始从URL获取用户ID...');
    // 获取当前URL
    const url = new URL(window.location.href)
    console.log('当前URL:', url.href);
    
    // 尝试从URL参数中获取用户ID相关信息
    // 常见的参数名包括userid, userId, id, uid等
    console.log('URL参数列表:', [...url.searchParams.entries()]);
    
    const userid = url.searchParams.get('userid');
    const userId = url.searchParams.get('userId');
    const id = url.searchParams.get('id');
    const uid = url.searchParams.get('uid');
    
    console.log('尝试获取的参数值:', {
      userid,
      userId,
      id,
      uid
    });
    
    const foundUserId = userid || userId || id || uid || null;
    
    if (foundUserId) {
      console.log('成功从URL获取用户ID:', foundUserId);
      // 创建或更新用户信息对象
      const existingUserInfo = getUicAuthUserInfo() || {}
      const userInfo = {
        ...existingUserInfo,
        userId: foundUserId,
        id: foundUserId, // 同时设置id字段以兼容不同的API要求
        lastUpdated: new Date().toISOString()
      }
      
      // 保存到存储中
      sessionStorage.setItem(UIC_AUTH_KEYS.userInfo, JSON.stringify(userInfo))
      localStorage.setItem(UIC_AUTH_KEYS.userInfo, JSON.stringify(userInfo))
      console.log('用户信息已保存到存储中');
      return true
    }
    console.log('URL中未找到有效的用户ID参数');
    return false
  } catch (error) {
    console.error('从URL获取用户ID失败:', error)
    return false
  }
}

/**
 * 获取用户ID
 * 优先从用户信息中获取，失败时尝试从URL获取
 * @returns {string|null} 用户ID
 */
export const getUserId = () => {
  console.log('调用getUserId()函数');
  // 优先从已保存的用户信息中获取
  const userInfo = getUicAuthUserInfo()
  console.log('从存储获取的用户信息:', userInfo);
  
  if (userInfo) {
    // 检查所有可能的用户ID字段名
    const userIdFields = ['userId', 'id', 'user_id', 'account'];
    for (const field of userIdFields) {
      if (userInfo[field]) {
        console.log(`从用户信息中获取到ID: ${userInfo[field]}`);
        return userInfo[field];
      }
    }
  }
  
  console.log('用户信息中未找到ID，尝试从URL获取');
  // 尝试从URL获取
  const urlResult = saveUserInfoFromUrl();
  console.log('从URL获取用户ID结果:', urlResult);
  
  // 再次尝试获取
  const updatedUserInfo = getUicAuthUserInfo();
  console.log('从URL处理后获取的用户信息:', updatedUserInfo);
  
  if (updatedUserInfo) {
    const userIdFields = ['userId', 'id', 'user_id', 'account'];
    for (const field of userIdFields) {
      if (updatedUserInfo[field]) {
        console.log(`从更新后的用户信息中获取到ID: ${updatedUserInfo[field]}`);
        return updatedUserInfo[field];
      }
    }
  }
  
  console.log('无法获取用户ID，返回默认值');
  return '1001'; // 作为最后兜底的默认值
}

/**
 * 清除所有uic-auth相关的存储信息
 */
export const clearUicAuthStorage = () => {
  // 清除sessionStorage中的uic-auth信息
  Object.values(UIC_AUTH_KEYS).forEach(key => {
    sessionStorage.removeItem(key)
  })
  
  // 清除localStorage中的uic-auth信息
  Object.values(UIC_AUTH_KEYS).forEach(key => {
    localStorage.removeItem(key)
  })
}

/**
 * 检查uic-auth的token是否存在
 * @returns {boolean} token是否存在
 */
export const hasUicAuthToken = () => {
  return !!getUicAuthToken()
}
